import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { Reveal } from '@/components/ui/reveal';
import LocaleLink from '@/components/LocaleLink';
import FrameworkLogo from '@/components/marketing/FrameworkLogo';
import { getFrameworkLogoDomain } from '@/data/frameworkLogos';
import { shippedHomepagePacks, packUrlId } from '@/data/contentPacks';
import { useMarketingTemplates } from '@/hooks/useMarketingTemplates';
import { FrameworkCoverageInline } from '@/components/marketing/FrameworkCoverageMatrix';

/**
 * Content pack grid (Messaging Architecture v1.0, homepage §4).
 *
 * Every tile names three specific regulations. A category word ("privacy
 * regulations") destroys the entire effect, because the whole claim is
 * specificity — so the regulation names are deliberately NOT translated.
 *
 * The packs, their regulations and the ship rule now live in
 * `src/data/contentPacks.ts`, next to the pack catalogue, so the homepage and
 * /templates cannot disagree about what a pack contains. A tile links to
 * `/templates?pack=<id>`, which filters the library to exactly the regulations
 * the tile just named — the tile's own promise, "See what's inside".
 *
 * Each row carries the issuer's mark and that regulation's live control count,
 * joined by template slug against the same query /templates runs. The count is
 * the section's argument in one number: a template gives you a checklist, a
 * regulation package gives you 278 cited controls. It is progressive
 * enhancement — the tile is complete without it, so a slow or failed fetch
 * costs a number and never a layout.
 *
 * Control count, not evidence count: DORA currently holds 215 controls and
 * zero evidence items, and "0 evidence" on the EU Regional tile would say the
 * opposite of what the section is claiming.
 *
 * FrameworkCoverageMatrix's summary block now sits inside this section rather
 * than beside it. The two were redundant: both were six-tile grids naming three
 * regulations per tile, a few hundred pixels apart, and the same regulations
 * appeared in both — SOC 2 and ISO 27001 under Security and again under
 * Americas, GDPR and CCPA under Privacy and again under Privacy Global. The
 * packs keep the grid, because a pack is a thing you adopt and carries a
 * control count. Coverage is breadth, and breadth reads fine as a rail of
 * numbers plus the lookup.
 */
// The i18n keys predate the pack catalogue slugs, so the tile headings still
// resolve through this map. The pack's own English name is the fallback.
// Packs that have earned a page of their own. Everything else points at the
// filtered library.

const PACK_I18N_KEY: Record<string, string> = {
  pack_eu_regional: 'euRegional',
  pack_federal_grants: 'federalGrants',
  pack_public_sector: 'publicSector',
  pack_privacy_global: 'privacyGlobal',
  pack_healthcare_plus: 'healthcarePlus',
  pack_americas_regional: 'americas',
};

const ContentPackGrid = () => {
  const { t } = useTranslation();
  const { data: blueprints = [] } = useMarketingTemplates();
  const controlsBySlug = useMemo(() => {
    const map = new Map<string, number>();
    for (const b of blueprints) {
      if (b.dbSlug && typeof b.controls === 'number' && b.controls > 0) map.set(b.dbSlug, b.controls);
    }
    return map;
  }, [blueprints]);

  return (
    <section className="bg-background py-8 lg:py-12 laptop:py-5">
      <div className="container mx-auto max-w-6xl px-6">
        <Reveal>
          <div className="mx-auto mb-6 laptop:mb-2 max-w-3xl text-center">
            <h2 className="font-bold text-foreground section-title-fluid">
              {t('contentPacks.title')}
            </h2>
            <p className="mt-4 laptop:mt-2 text-lg text-muted-foreground">
              {t('contentPacks.subtitle')}
            </p>
          </div>
        </Reveal>

        {/* Breadth first, depth under it. This block used to sit below the pack
            tiles and pushed the section past a screen; the tiles are the thing
            worth ending on, and the rail reads as the setup for them rather
            than an afterthought. The #coverage anchor moves with it. */}
        <div id="coverage" className="mb-7 laptop:mb-3 scroll-mt-24 border-b border-border pb-5 laptop:pb-4">
          <FrameworkCoverageInline />
        </div>

        <div className="grid gap-4 laptop:gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {shippedHomepagePacks().map((pack, idx) => (
            <Reveal key={pack.slug} delay={idx * 0.05}>
              <LocaleLink
                to={
                  // A pack with a page that makes its own argument goes there;
                  // the rest land on the filtered library, which is the argument
                  // for them.
                  pack.program
                    ? `/programs/${packUrlId(pack)}`
                    : `/templates?pack=${packUrlId(pack)}`
                }
                className="group flex h-full flex-col border border-border bg-secondary/50 p-5 laptop:p-4 transition-colors hover:border-trail-teal hover:bg-secondary"
              >
                <h3 className="text-base font-semibold text-foreground">
                  {t(`contentPacks.packs.${PACK_I18N_KEY[pack.slug]}`, pack.name)}
                </h3>
                <ul className="mt-4 laptop:mt-2 flex-1 space-y-3 laptop:space-y-2">
                  {(pack.tileRegulations ?? []).map(({ label: reg, slug }) => {
                    const controls = controlsBySlug.get(slug);
                    return (
                      <li key={slug} className="flex items-center gap-2.5">
                        {/* The issuing body's mark where we have one, the brand
                            bullet where we do not. Both reserve the same box, so
                            a slow or failed Logo.dev fetch never shifts the row. */}
                        {getFrameworkLogoDomain(reg) ? (
                          <FrameworkLogo name={reg} size={24} color />
                        ) : (
                          <span aria-hidden className="h-1.5 w-1.5 shrink-0 bg-trail-teal" />
                        )}
                        {/* Name and count on one line. The name always renders,
                            so the row height is fixed and a late count cannot
                            shift the tile. */}
                        <span className="min-w-0 text-sm leading-snug">
                          <span className="font-medium text-foreground">{reg}</span>
                          {controls ? (
                            <span className="ml-1.5 font-mono text-xs text-muted-foreground">
                              {t('contentPacks.controlCount', { count: controls })}
                            </span>
                          ) : null}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <span className="mt-4 laptop:mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary">
                  {t('contentPacks.tileCta')}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </LocaleLink>
            </Reveal>
          ))}
        </div>

      </div>
    </section>
  );
};

export default ContentPackGrid;
